import { Icon } from '@gftg/ui';
import { Link } from 'react-router';
import { Avatar } from '../components/Avatar';
import { CategoryChip } from '../components/Chips';
import { LinkedInButton } from '../components/LinkedInButton';
import { EmptyBlock, ErrorBlock, LoadingBlock, PageHeader } from '../components/states';
import { formatMonthDay } from '../lib/datetime';
import { roleLine } from '../lib/people';
import { useEvent, useMatches, type Match } from '../lib/queries';

/**
 * Concierge Connections (module 7): the organizers press Connect, the run pairs everybody from their
 * questionnaire answers, and this screen reads the result once it is published. There is nothing to
 * accept, decline or request — the prototype's connection buttons belong to a release that has them.
 *
 * The copy has to be true of the thing that produced these. Every reason on this screen names what
 * the two of them actually share, so the screen may say the introductions were found from their
 * answers — but not that anybody read the two profiles and thought about it, and not that the list
 * is ordered by anything richer than the count of those shared answers.
 */
export function MatchesPage() {
  const event = useEvent();
  const matches = useMatches();

  if (matches.isPending) {
    return <LoadingBlock label="Loading your matches…" />;
  }

  if (matches.isError || !matches.data) {
    return (
      <ErrorBlock
        message="Your matches could not be loaded."
        onRetry={() => void matches.refetch()}
      />
    );
  }

  const { isPublished, publishedOn, matches: list } = matches.data;

  if (list.length === 0) {
    return (
      <>
        <PageHeader title="My matches" />
        <main className="px-gutter pb-6">
          {isPublished ? (
            <EmptyBlock title="No introductions for you in this round">
              These are found from your questionnaire — your industry, your interests and what you
              came for — so the more of it you have filled in, the more we can find. Check that{' '}
              <Link to="/account/questionnaire" className="font-semibold text-ink underline">
                Include me in Concierge Connections
              </Link>{' '}
              is on, and add your answers before the next round.
            </EmptyBlock>
          ) : (
            <EmptyBlock title="Your introductions are not ready yet">
              The organizers put these together shortly before the event, from what everybody
              answered in their questionnaire. You will get an email when yours are ready.
            </EmptyBlock>
          )}
        </main>
      </>
    );
  }

  return (
    <>
      {/* The only dark header in the app, as the design has it: these are the people the run found
          for this attendee, and the screen says where they came from before it says anything else. */}
      <header className="px-gutter bg-ink pt-3 pb-6 text-white">
        <div className="flex items-center gap-3">
          <Link
            to="/"
            aria-label="Back"
            className="flex size-tap shrink-0 items-center justify-center rounded-full bg-white/10 text-white"
          >
            <Icon name="chevron-left" size={18} />
          </Link>
          {publishedOn && event.data && (
            <p className="text-eyebrow tracking-eyebrow font-bold text-brand-light uppercase">
              Published {formatMonthDay(publishedOn, event.data.timezone)}
            </p>
          )}
        </div>

        <h1 className="font-display text-screen-title mt-4 font-semibold">
          {list.length} {list.length === 1 ? 'introduction' : 'introductions'} for you
        </h1>
        {/* What the order actually is, said plainly. "Ranked by fit" would promise a judgement
            nothing here makes: the first card is simply the one with the most in common, and the
            reason underneath it lists exactly what that was. */}
        <p className="text-subtitle mt-1 text-white/70">
          Found from your questionnaire, most in common first
        </p>
      </header>

      {/* Two to a row on a laptop, in the same reading order: the first card is still the one with
          the most in common, and the number on each says where it came in. */}
      <main className="px-gutter space-y-card py-4 lg:grid lg:grid-cols-2 lg:gap-card lg:space-y-0">
        {list.map((match) => (
          <MatchCard key={match.person.id} match={match} />
        ))}
      </main>
    </>
  );
}

function MatchCard({ match }: { match: Match }) {
  const { person, rank, reason } = match;
  const role = roleLine(person.jobTitle, person.company);

  return (
    <article className="rounded-card border border-border bg-surface p-4">
      <div className="flex gap-3">
        <Avatar name={person.name} photoAssetId={person.photoAssetId} />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-row min-w-0 font-semibold text-ink">{person.name}</h2>
            <span className="text-subtitle shrink-0 text-ink-4 tabular-nums">
              {String(rank).padStart(2, '0')}
            </span>
          </div>
          {role && <p className="text-subtitle mt-0.5 text-ink-3">{role}</p>}
          <span className="mt-2 inline-block">
            <CategoryChip label={person.category} />
          </span>
        </div>
      </div>

      {/* Serif italic, the one place the kit uses it: the reason reads as a sentence, not a field —
          and it is the same sentence the other attendee is reading about this one. */}
      <p className="font-display text-body mt-3 text-ink-2 italic">{reason}</p>

      <div className="mt-4 flex gap-2">
        {person.linkedinUrl && <LinkedInButton url={person.linkedinUrl} grow />}
        <Link
          to={`/directory/${person.id}`}
          className="text-button rounded-button flex min-h-touch flex-1 basis-0 items-center justify-center bg-ink px-3 font-semibold whitespace-nowrap text-white"
        >
          View profile
        </Link>
      </div>
    </article>
  );
}
